import { useState, useContext } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API, AuthContext } from "../App";
import PasswordInput from "../components/PasswordInput";

const MEGA_LOGO = "https://customer-assets.emergentagent.com/job_80904b6f-7286-4784-ad3d-292b160c5a80/artifacts/crjane52_MEGA%20CHAT%20LOGO.png";
const MESSENGER_LOGO = "/messenger-logo.png";

const LoginPage = () => {
  const { login } = useContext(AuthContext);
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const isMessenger = params.get("app") === "messenger";
  const refUser = params.get("ref") || "";
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [guestLoading, setGuestLoading] = useState(false);
  const [showGuestName, setShowGuestName] = useState(false);
  const [guestName, setGuestName] = useState("");
  const [nameLoading, setNameLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/login`, { 
        identifier: identifier,
        password: password,
        source: isMessenger ? "messenger" : "chat",
      });
      login(response.data.access_token, response.data.user);
      toast.success("Welcome back!");
      if (isMessenger) {
        // Land the user inside the Messenger app after a messenger login
        setTimeout(() => navigate("/messenger"), 50);
      }
    } catch (error) {
      console.error("Login error:", error);
      toast.error(error.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const openGuestPicker = async () => {
    setShowGuestName(true);
    const saved = typeof window !== "undefined" ? localStorage.getItem("mega_guest_name") : "";
    if (saved) {
      setGuestName(saved);
    } else {
      setGuestName("");
      await rerollGuestName();
    }
  };

  const rerollGuestName = async () => {
    setNameLoading(true);
    try {
      const res = await axios.get(`${API}/auth/guest/suggest`);
      setGuestName(res.data.username);
    } catch (_) {
      setGuestName("");
    } finally {
      setNameLoading(false);
    }
  };

  const handleGuestLogin = async () => {
    setGuestLoading(true);
    try {
      const response = await axios.post(`${API}/auth/guest`, guestName ? { username: guestName } : {});
      try { localStorage.setItem("mega_guest_name", response.data.user.username); } catch (_) { /* noop */ }
      login(response.data.access_token, response.data.user);
      toast.success(`Welcome, ${response.data.user.username}! Hop into GUEST CHAT.`);
    } catch (error) {
      console.error("Guest login error:", error);
      toast.error(error.response?.data?.detail || "Guest login failed");
    } finally {
      setGuestLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0a0a1f] via-[#1a0a3e] to-[#3a0a5c] flex items-start sm:items-center justify-center p-2 sm:p-4 py-6 relative">
      {/* Giant centered background logo watermark — fixed so it stays visible while the page scrolls */}
      <img
        src={isMessenger ? MESSENGER_LOGO : MEGA_LOGO}
        alt=""
        aria-hidden="true"
        className="fixed inset-0 m-auto w-[85vmin] h-[85vmin] max-w-none object-contain opacity-[0.10] pointer-events-none select-none z-0"
      />
      <div className="retro-window w-full max-w-sm relative z-10 shadow-2xl shadow-black/50" data-testid="login-window">
        <div className="retro-title-bar">
          <span>{isMessenger ? "MEGA! Messenger - Sign In" : "MEGA! Chat - Sign In"}</span>
        </div>
        
        <div className="p-4 bg-[#C0C0C0]">
          {/* Logo — smaller so the whole window fits on one screen */}
          <div className="text-center mb-3">
            <img 
              src={isMessenger ? MESSENGER_LOGO : MEGA_LOGO} 
              alt={isMessenger ? "MEGA Chat Messenger" : "MEGA Chat - World Wide Chat Rooms"}
              className="w-36 h-36 sm:w-44 sm:h-44 mx-auto object-contain drop-shadow-lg"
              data-testid="mega-chat-logo"
            />
            {isMessenger && (
              <p className="font-verdana text-xs text-[#7B0099] font-bold mt-1" data-testid="messenger-signin-label">
                Sign in to MEGA Chat Messenger
              </p>
            )}
          </div>

          <form onSubmit={handleSubmit} className="space-y-3">
            <div>
              <label className="block font-verdana text-xs mb-1">Username or Email:</label>
              <input
                type="text"
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                className="retro-input w-full"
                required
                placeholder="Enter username or email"
                data-testid="login-email-input"
              />
            </div>

            <div>
              <label className="block font-verdana text-xs mb-1">Password:</label>
              <PasswordInput
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="retro-input w-full"
                required
                testid="login-password-input"
              />
            </div>

            <div className="flex items-center justify-between text-[11px]">
              <div className="flex items-center gap-2">
                <input type="checkbox" id="remember" className="w-3 h-3" />
                <label htmlFor="remember" className="font-arial">Remember me</label>
              </div>
              <Link 
                to="/forgot-password" 
                className="font-arial text-[#0000EE] hover:underline"
                data-testid="forgot-password-link"
              >
                Forgot Password?
              </Link>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="retro-button w-full py-1.5"
              data-testid="login-submit-btn"
            >
              {loading ? "Signing In..." : "Sign In"}
            </button>
          </form>

          {!isMessenger && (
            <div className="mt-2">
              <button
                type="button"
                onClick={openGuestPicker}
                disabled={guestLoading || loading}
                className="retro-button w-full py-1.5 bg-[#FFE9A8] border-[#B8860B] font-bold"
                data-testid="guest-login-btn"
              >
                {guestLoading ? "Starting..." : "👤 Continue as Guest"}
              </button>
              <p className="font-arial text-[10px] text-center text-[#555] mt-1" data-testid="guest-login-hint">
                Guests can only chat in <b>GUEST CHAT</b>. Register free to unlock all rooms.
              </p>
            </div>
          )}

          <div className="mt-3 pt-2 border-t-2 border-[#808080]">
            <p className="font-arial text-xs text-center">
              New here?{" "}
              <Link to={isMessenger ? `/register?app=messenger${refUser ? `&ref=${encodeURIComponent(refUser)}` : ""}` : "/register"} className="text-[#0000EE] hover:underline" data-testid="register-link">
                Sign Up
              </Link>
            </p>
          </div>

          <div className="mt-2 p-1.5 bg-[#FFCC00] text-center">
            <p className="font-arial text-[11px] font-bold text-[#7B0099]">
              Chat with friends from all 50 states or around the world!
            </p>
          </div>

          <div className="mt-2 text-center">
            <p className="font-arial text-[10px] text-[#808080]">created by mr_fuker</p>
          </div>
        </div>
      </div>

      {showGuestName && (
        <div className="fixed inset-0 z-[9900] bg-black/60 flex items-center justify-center p-4" onClick={() => setShowGuestName(false)} data-testid="guest-name-modal">
          <div className="retro-window w-80 max-w-full" onClick={(e) => e.stopPropagation()}>
            <div className="retro-title-bar">
              <span>👤 Pick a Guest Name</span>
              <button className="retro-button px-2 py-0 text-xs" onClick={() => setShowGuestName(false)} data-testid="guest-name-close">×</button>
            </div>
            <div className="p-4 bg-[#C0C0C0] text-center">
              <p className="font-arial text-[11px] text-[#555] mb-3">This is your temporary chat name. Hit refresh for a different one!</p>
              <div className="bg-white border-2 border-[#808080] rounded py-3 px-2 mb-3">
                <span className="font-verdana text-lg font-bold text-[#7B0099]" data-testid="guest-name-value">
                  {nameLoading ? "…" : (guestName || "Guest")}
                </span>
              </div>
              <button
                type="button"
                onClick={rerollGuestName}
                disabled={nameLoading}
                className="retro-button w-full py-1.5 mb-2"
                data-testid="guest-name-refresh"
              >
                🔄 New Name
              </button>
              <button
                type="button"
                onClick={handleGuestLogin}
                disabled={guestLoading || nameLoading || !guestName}
                className="retro-button w-full py-1.5 bg-[#7B0099] text-white border-[#7B0099] font-bold"
                data-testid="guest-name-enter"
              >
                {guestLoading ? "Entering..." : "Enter Chat →"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LoginPage;
